import React, { useState, useRef, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SentenceTemplate, { SentenceTemplateValues, ValidationState, SentenceTemplateRef } from './components/SentenceTemplate';
import { LoadingScreen } from './components/LoadingScreen';
import { FullscreenButton } from './components/FullscreenButton';
import { AppSkeleton } from './components/AppSkeleton';
import { initCache } from './services/cacheService';
import { orchestrateGeneration, type AssetSource } from './services/orchestrationService';
import { isInIframe, isIframeConstrained, setupIframeFocusIndicator } from './utils/iframeDetection';
import type { CropMetadata } from './types/cache';
import './App.css';
import './styles/iframe.css';

/**
 * Story 7.3 Task 2: Lazy load GameComponent for progressive enhancement
 * This enables chunk splitting and allows critical UI to load first
 */
const GameComponent = lazy(() => import('./components/GameComponent'));

// Story 1.3: Curated example lists for Try Random feature
const CHARACTER_OPTIONS = [
  'dragon', 'astronaut', 'ninja', 'penguin', 'robot',
  'wizard', 'pirate', 'knight', 'cat', 'tiger',
  'superhero', 'alien', 'vampire', 'ghost', 'unicorn'
] as const;

const OBSTACLE_OPTIONS = [
  'volcano', 'skyscraper', 'tree', 'iceberg', 'mountain',
  'waterfall', 'canyon', 'cliff', 'rock', 'pillar',
  'cactus', 'tower', 'statue', 'wall', 'gate'
] as const;

const BACKGROUND_OPTIONS = [
  'forest', 'city', 'space', 'ocean', 'desert',
  'cave', 'sky', 'sunset', 'snow', 'jungle',
  'lava', 'underwater', 'clouds', 'night', 'beach'
] as const;

function GamePage() {
  const location = useLocation();
  const isBlackHumorPage = location.pathname === '/black-humor';

  // Ref to SentenceTemplate for validation and focus management
  const sentenceTemplateRef = useRef<SentenceTemplateRef>(null);

  // Ref to Generate button for focus restoration (Story 4.1 - AC4)
  const generateButtonRef = useRef<HTMLButtonElement>(null);

  // Story 4.2: Ref to store timer for cleanup
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // Story 4.3: AbortController for generation cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // State for sentence template values (Story 1.2 - lifted from SentenceTemplate)
  const [values, setValues] = useState<SentenceTemplateValues>({
    character: '',
    obstacle: '',
    background: '',
  });

  // State for validation (Story 1.2)
  const [validationState, setValidationState] = useState<ValidationState>({
    characterValid: false,
    obstacleValid: false,
    backgroundValid: false,
    characterError: '',
    obstacleError: '',
    backgroundError: '',
  });

  // State for loading screen (Story 4.1)
  const [isGenerating, setIsGenerating] = useState(false);

  // Story 4.2: Progress state for sequential indicators
  // Story 6.2: Added partialFailures for demo mode messaging
  const [generationProgress, setGenerationProgress] = useState<{
    currentAsset: 'character' | 'obstacle' | 'background';
    progress: number;
    elapsedTime: number;
    partialFailures?: {
      failedAssets: Array<'character' | 'obstacle' | 'background'>;
      messages: string[];
    };
  }>({
    currentAsset: 'character',
    progress: 0,
    elapsedTime: 0,
  });

  // Story 4.3: Asset sources tracking (custom/demo/cached)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [assetSources, setAssetSources] = useState<Record<string, AssetSource>>({});

  // Story 5.2: Custom assets for game rendering
  const [customAssets, setCustomAssets] = useState<{
    assets: {
      character?: string;
      obstacle?: string;
      background?: string;
    };
    metadata: {
      character?: CropMetadata;
      obstacle?: CropMetadata;
      background?: CropMetadata;
    };
  } | null>(null);

  // Story 4.4: Timeout warning state (triggered at 20 seconds)
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Story 7.2: iframe detection state
  const [inIframe, setInIframe] = useState(false);
  const [iframeConstrained, setIframeConstrained] = useState(false);

  // Check if all fields are valid
  const isGenerateEnabled =
    validationState.characterValid &&
    validationState.obstacleValid &&
    validationState.backgroundValid;

  // Track previous active element for Escape key focus restoration
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const currentActiveElementRef = useRef<HTMLElement | null>(null);

  /**
   * Story 3.1: Initialize IndexedDB cache on app load
   */
  useEffect(() => {
    initCache().then((db) => {
      if (db) {
        console.log('Cache initialized successfully');
      } else {
        console.log('Cache initialization failed - running in API-only mode');
      }
    }).catch((error) => {
      console.error('Cache initialization error:', error);
    });
  }, []);

  /**
   * Story 7.2: iframe detection and focus indicator setup
   */
  useEffect(() => {
    // Detect iframe context
    const isIframe = isInIframe();
    setInIframe(isIframe);

    if (isIframe) {
      // Check if iframe is constrained
      setIframeConstrained(isIframeConstrained());

      // Add iframe-focused class support
      const cleanup = setupIframeFocusIndicator();

      // Add window resize listener to update constrained state
      const handleResize = () => {
        setIframeConstrained(isIframeConstrained());
      };

      window.addEventListener('resize', handleResize);

      return () => {
        cleanup();
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  /**
   * Story 4.2/4.3: Cleanup timer, timeouts, and abort controller on unmount
   */
  useEffect(() => {
    return () => {
      // Clear interval timer if exists
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      // Clear all timeouts
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));

      // Story 4.3: Cancel ongoing generation requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Story 6.3 Task 2: Monitor offline/online events during generation
   * Aborts generation and triggers automatic demo mode if browser goes offline
   */
  useEffect(() => {
    const handleOffline = () => {
      console.log('Offline event detected during generation');

      // Only trigger if currently generating
      if (isGenerating && abortControllerRef.current) {
        console.log('Aborting generation due to offline status');
        abortControllerRef.current.abort();

        // Note: orchestrationService.ts already handles offline check at start (line 613-616)
        // This handler catches mid-generation offline events
        // The abort will cause orchestrateGeneration to fail, triggering demo fallback
      }
    };

    const handleOnline = () => {
      console.log('Online event detected - connection restored');
      // Optional: Could show notification to user that they can retry generation
    };

    // Story 6.3 Task 2: Add event listeners for offline/online detection
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // Story 6.3 Task 2: Cleanup event listeners
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [isGenerating]);

  /**
   * Story 1.4: Handle Escape key to restore previous focus or clear focus
   * Pressing Escape restores focus to previous element, or clears focus if none
   * Story 4.1: Disable Escape handler when loading screen is active
   */
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      // Store current as previous before updating current
      if (currentActiveElementRef.current && currentActiveElementRef.current !== document.body) {
        previousActiveElementRef.current = currentActiveElementRef.current;
      }
      // Update current
      if (event.target && event.target !== document.body) {
        currentActiveElementRef.current = event.target as HTMLElement;
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      // Story 4.1: Disable Escape key when loading screen is active
      if (isGenerating) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault(); // Prevent default browser behavior

        const currentElement = document.activeElement as HTMLElement;

        // Process escape if an element is focused
        if (currentElement && currentElement !== document.body) {
          // Restore focus to previous element if it exists and is valid
          if (previousActiveElementRef.current &&
              previousActiveElementRef.current !== currentElement &&
              document.body.contains(previousActiveElementRef.current)) {
            previousActiveElementRef.current.focus();
          } else {
            // No previous element - just blur current
            currentElement.blur();
          }
        }
      }
    };

    // Add event listeners
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('keydown', handleEscapeKey);

    // Cleanup
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isGenerating]);

  /**
   * Story 1.3: Get random value from array, excluding current value
   * @param options - Array of possible values
   * @param excludeValue - Value to exclude from selection (ensures different value on each click)
   * @returns Random value from options (excluding excludeValue if provided)
   */
  const getRandomValue = (options: readonly string[], excludeValue?: string): string => {
    // Defensive check: ensure options array is not empty
    if (!options || options.length === 0) {
      console.error('getRandomValue called with empty options array');
      return '';
    }

    // Filter out current value to ensure different selection
    let availableOptions = excludeValue
      ? options.filter(opt => opt !== excludeValue)
      : [...options];

    // Edge case: if all options excluded, use full list
    if (availableOptions.length === 0) {
      availableOptions = [...options];
    }

    // Random selection
    const randomIndex = Math.floor(Math.random() * availableOptions.length);
    return availableOptions[randomIndex] || '';
  };

  /**
   * Story 1.3: Handles Try Random button click
   * Populates all three fields with random pre-validated values
   */
  const handleTryRandom = () => {
    const newCharacter = getRandomValue(CHARACTER_OPTIONS, values.character);
    const newObstacle = getRandomValue(OBSTACLE_OPTIONS, values.obstacle);
    const newBackground = getRandomValue(BACKGROUND_OPTIONS, values.background);

    setValues({
      character: newCharacter,
      obstacle: newObstacle,
      background: newBackground,
    });

    // Set all fields to valid (pre-validated values)
    // Note: Story 1.2 hasBlurred state in SentenceTemplate.tsx (lines 87-91) manages
    // error display timing. Pre-validated values trigger validation automatically
    // through controlled props - DO NOT directly manipulate hasBlurred state.
    setValidationState({
      characterValid: true,
      obstacleValid: true,
      backgroundValid: true,
      characterError: '',
      obstacleError: '',
      backgroundError: '',
    });
  };

  /**
   * Handles generate button click
   * Story 4.3: Real orchestration with cache-first strategy, API integration, and demo fallback
   */
  const handleGenerate = async () => {
    // Validate all fields and focus first invalid field
    const isValid = sentenceTemplateRef.current?.validateAndFocus();

    if (!isValid) {
      // Validation failed - errors are now shown and focus is set
      return;
    }

    // All fields valid - proceed with generation
    // Story 4.1: Show loading screen
    setIsGenerating(true);

    // Story 4.2: Initialize progress state
    setGenerationProgress({ currentAsset: 'character', progress: 0, elapsedTime: 0 });

    // Story 4.2: Clear any existing timers
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    timeoutsRef.current = [];

    // Story 4.3: Create new AbortController for this generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Story 4.2: Track elapsed time with setInterval
    let elapsed = 0;
    timerIntervalRef.current = setInterval(() => {
      elapsed += 1;

      // Story 4.4: Check for 20s timeout warning (callback form prevents race condition)
      if (elapsed >= 20) {
        setShowTimeoutWarning(prev => prev ? prev : true);
      }

      setGenerationProgress((prev) => ({ ...prev, elapsedTime: elapsed }));
    }, 1000);

    try {
      // Story 4.3: Initialize cache
      const db = await initCache();

      // Story 4.3: Orchestrate generation with progress callback
      const result = await orchestrateGeneration(
        {
          character: values.character,
          obstacle: values.obstacle,
          background: values.background,
        },
        db,
        (currentAsset, progress, elapsedTime, partialFailures) => {
          setGenerationProgress({ currentAsset, progress, elapsedTime, partialFailures });
        },
        abortControllerRef.current.signal
      );

      // Story 4.3: Store asset sources for future analytics
      setAssetSources(result.assetSources);

      // Log completion summary
      console.log('Generation complete:', {
        state: result.state,
        sources: result.assetSources,
        duration: result.totalDuration,
      });

      // Story 5.2: Save generated assets and metadata for GameComponent
      setCustomAssets({
        assets: result.assets,
        metadata: result.assetMetadata,
      });
      console.log('Assets generated and saved:', Object.keys(result.assets));
    } catch (error) {
      // Handle abort or unexpected errors
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Generation cancelled by user');
      } else {
        console.error('Unexpected generation error:', error);
      }
    } finally {
      // Cleanup: Stop timer
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Story 4.4: Reset timeout warning
      setShowTimeoutWarning(false);

      // Hide loading screen
      setIsGenerating(false);

      // Story 4.1 - AC4: Restore focus to Generate button
      generateButtonRef.current?.focus();
    }
  };

  return (
    <>
      <Helmet>
        <title>{isBlackHumorPage ? 'Flight Simulator' : 'GenFlappy - Customize Your Game'}</title>
        <meta name="description" content={isBlackHumorPage ? 'Flight simulator game' : 'Play the classic Flappy Bird game - Test your skills and beat your high score in this addictive arcade game!'} />
        <meta name="robots" content={isBlackHumorPage ? 'noindex, nofollow' : 'index, follow'} />
      </Helmet>
      <div className={`app-container max-w-3xl mx-auto ${iframeConstrained ? 'iframe-constrained' : ''}`}>
        <h1 className="main-heading">Customize your Flappy Bird game with AI</h1>
        {!isGenerating && !customAssets && (
          <>
            <SentenceTemplate
              ref={sentenceTemplateRef}
              values={values}
              onChange={setValues}
              validationState={validationState}
              onValidationChange={setValidationState}
            />
            <div className="button-row">
              <button
                className="try-random-button"
                onClick={handleTryRandom}
                aria-label="Try random examples - fills all three fields with sample prompts"
              >
                I'M FEELING LUCKY
              </button>
              <button
                ref={generateButtonRef}
                className="generate-button"
                disabled={!isGenerateEnabled || isGenerating}
                aria-disabled={!isGenerateEnabled || isGenerating}
                aria-label={
                  isGenerateEnabled
                    ? 'Generate game with your custom prompts'
                    : 'Generate button disabled. Please fill all fields with valid values.'
                }
                onClick={handleGenerate}
              >
                LET'S RUN
              </button>
            </div>
          </>
        )}
        <LoadingScreen
          isVisible={isGenerating}
          currentAsset={generationProgress.currentAsset}
          progress={generationProgress.progress}
          elapsedTime={generationProgress.elapsedTime}
          timeoutWarning={showTimeoutWarning}
          partialFailures={generationProgress.partialFailures}
        />
        {/* Story 7.3 Task 2: Suspense wrapper for lazy-loaded GameComponent */}
        <Suspense fallback={<AppSkeleton />}>
          <div className={`game-wrapper ${customAssets ? 'game-visible' : 'game-hidden'}`}>
            <GameComponent
              customAssets={customAssets}
              onBackClick={() => setCustomAssets(null)}
            />
          </div>
        </Suspense>
      </div>

      {/* Story 7.2: Fullscreen button for constrained iframes */}
      <FullscreenButton show={inIframe && iframeConstrained} />
    </>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<GamePage />} />
        <Route path="/black-humor" element={<GamePage />} />
      </Routes>
    </Router>
  );
}

export default App;